import { P, Stack, Text, useSigning, useText } from "@sveabank/cc-common";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import IconWithText from "../../components/icon-with-text";
import PackageSignBox from "../../components/sign-box/package-signbox";
import { usePageContext } from "../../hooks/context";
import { useSteps } from "../../hooks/useSteps";
import { useLazyFetchGuarantorAgreementQuery } from "../../services/guarantors";
import {
	useCancelSignMutation,
	useGetSigningStatusMutation,
	useInitializeGuarantorSigningMutation,
} from "../../services/signing";
import { setError } from "../../slices/errorSlice";
import { normalizeError } from "../../utils/error-handling";
import AgreementSigningModal from "./agreement-signing";
import GuarantorSuccessModal from "./guarantor-success-modal";
import { checkSignatorSigningStatus, transformGuarantorsToSigner } from "./helpers";
import { useSignInformation } from "./hooks/hooks";

type Props = {
	altText?: string;
};

const GuarantorSection = ({ altText }: Props) => {
	const dispatch = useDispatch();
	const [t, texts] = useText();

	const {
		applicationId,
		isSignGuarantorsCompleted,
		hasCurrentUserSignedGuarantors,
		guarantorsSignStatuses,
		guarantors,
		currentUserNationalId,
		setGuarantorStepHasModalOpen,
	} = usePageContext(useSignInformation);
	const { someStepExists, refetchSteps } = useSteps();

	const [
		fetchGuarantorAgreement,
		{ data: guarantorAgreement, isFetching: isFetchingGuarantorAgreement },
	] = useLazyFetchGuarantorAgreementQuery();
	const { requestWithProductSigning } = useSigning();
	const [initGuarantorSigningRequest] = useInitializeGuarantorSigningMutation();
	const [getSigningStatus] = useGetSigningStatusMutation();
	const [cancelSign] = useCancelSignMutation();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const guarantorSigners = transformGuarantorsToSigner(guarantors);

	const handleOpenSignModal = async () => {
		await fetchGuarantorAgreement(applicationId).unwrap();
		setIsModalOpen(true);
	};

	useEffect(() => {
		setGuarantorStepHasModalOpen(isModalOpen || isSuccessModalOpen);
	}, [isModalOpen, isSuccessModalOpen]);

	const handleSigning = async () => {
		const init = () => initGuarantorSigningRequest({ applicationId }).unwrap();
		const status = () => getSigningStatus({ applicationId }).unwrap();
		const cancel = () => cancelSign(applicationId).unwrap();

		try {
			const request = requestWithProductSigning(init, status, cancel);

			await request(applicationId);
			await refetchSteps().unwrap();
			setIsModalOpen(false);
			setIsSuccessModalOpen(true);
		} catch (error) {
			if (error instanceof Error) {
				if (error.message === "BankIdTimeout") {
					dispatch(setError(normalizeError(error)));
					return;
				}
			}
			throw error;
		}
	};

	return (
		<Stack gap={4}>
			{altText ? (
				<P>{altText}</P>
			) : (
				<P>
					{t(texts.signPage.guarantors.description)}
					<Text bold>{t(texts.signPage.signing.guarantorSign.descriptionBold)}</Text>
				</P>
			)}

			{guarantorSigners.map((guarantor) => (
				<PackageSignBox
					key={guarantor.nationalId}
					signer={guarantor}
					isSigned={checkSignatorSigningStatus(guarantor, guarantorsSignStatuses)}
					signable={
						guarantor.nationalId === currentUserNationalId &&
						someStepExists("SignGuarantorAgreementStep")
					}
					handleOpenSignModal={handleOpenSignModal}
					isFetchingAgreement={isFetchingGuarantorAgreement}
				/>
			))}

			{hasCurrentUserSignedGuarantors && !isSignGuarantorsCompleted && (
				<IconWithText
					text={t(texts.signPage.package.needMoreGuarantorSignaturesWarning)}
					imageSrc="WARNING_SIGN"
				/>
			)}

			<GuarantorSuccessModal
				isOpen={isSuccessModalOpen}
				onClose={() => {
					setIsSuccessModalOpen(false);
				}}
			/>

			{guarantorAgreement && (
				<AgreementSigningModal
					isModalOpen={isModalOpen}
					setOpen={setIsModalOpen}
					title={t(texts.signPage.package.guarantorSigningTitle)}
					signAction={handleSigning}
					isSigning={false}
					pdfData={guarantorAgreement}
				/>
			)}
		</Stack>
	);
};

export default GuarantorSection;
